import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Field, SelectField } from "@/components/common/Field";
import { Combobox } from "@/components/common/Combobox";
import { VEHICLE_MAKES, getModelsForMake } from "@/lib/vehicleData";
import { DealershipFormDialog } from "@/components/dealerships/DealershipFormDialog";
import { Loader } from "@/components/common/Loader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { FUEL_TYPES, CONDITIONS, ALLOY_OPTIONS, YEARS } from "@/lib/constants";
import {
  ArrowLeft, Store, User, Car, Plus, Loader2, Info, Save,
} from "lucide-react";
import { cn } from "@/lib/utils";

const EMPTY = {
  customer_name: "", customer_mobile: "", customer_alt_mobile: "", customer_city: "",
  customer_address: "", customer_remarks: "",
  vehicle_number: "", make: "", model: "", variant: "", year: "2011", fuel_type: "",
  km: "", alloy: "", vehicle_condition: "", demand: "", offer_price: "", remarks: "",
};

export default function NewLead({ editMode = false }) {
  const navigate = useNavigate();
  const { id } = useParams();
  const [sp] = useSearchParams();

  const [leadType, setLeadType] = useState(sp.get("type") || "dealership");
  const [dealershipId, setDealershipId] = useState(sp.get("dealership_id") || "");
  const [dealerships, setDealerships] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(editMode);
  const [dealerDialog, setDealerDialog] = useState(false);

  const loadDealers = useCallback(() => {
    return api.get("/dealerships").then((res) => setDealerships(res.data));
  }, []);

  useEffect(() => { loadDealers(); }, [loadDealers]);

  useEffect(() => {
    if (!editMode) return;
    api.get(`/leads/${id}`)
      .then((res) => {
        const l = res.data.lead;
        setLeadType(l.lead_type);
        setDealershipId(l.dealership_id || "");
        setForm({
          ...EMPTY,
          ...Object.fromEntries(Object.keys(EMPTY).map((k) => [k, l[k] ?? ""])),
        });
      })
      .catch(() => toast.error("Could not load lead"))
      .finally(() => setLoading(false));
  }, [editMode, id]);

  const set = (k) => (e) => {
    const v = e?.target ? e.target.value : e;
    setForm((f) => ({ ...f, [k]: k === "vehicle_number" ? v.toUpperCase() : v }));
  };

  // Changing Make resets the dependent Model selection
  const handleMake = (v) => setForm((f) => ({ ...f, make: v, model: "" }));

  const buildPayload = () => {
    const num = (v) => (v === "" || v === null || v === undefined ? null : Number(v));
    const str = (v) => (v === "" ? null : v);
    const base = {
      lead_type: leadType,
      dealership_id: leadType === "dealership" ? (dealershipId || null) : null,
      vehicle_number: str(form.vehicle_number),
      make: str(form.make), model: str(form.model), variant: str(form.variant),
      year: str(form.year), fuel_type: str(form.fuel_type), alloy: str(form.alloy),
      vehicle_condition: str(form.vehicle_condition), demand: num(form.demand),
      offer_price: num(form.offer_price), remarks: str(form.remarks),
    };
    if (leadType === "individual") {
      Object.assign(base, {
        customer_name: str(form.customer_name), customer_mobile: str(form.customer_mobile),
        customer_alt_mobile: str(form.customer_alt_mobile), customer_city: str(form.customer_city),
        customer_address: str(form.customer_address), customer_remarks: str(form.customer_remarks),
        km: str(form.km),
      });
    }
    return base;
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = buildPayload();
      if (editMode) {
        await api.put(`/leads/${id}`, payload);
        toast.success("Lead updated");
        navigate(`/leads/${id}`, { replace: true });
      } else {
        const res = await api.post("/leads", payload);
        toast.success("Lead created");
        navigate(`/leads/${res.data.id}`, { replace: true });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save lead");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loader label="Loading lead…" />;

  const dealerOptions = dealerships.map((d) => ({
    value: d.id, label: d.oem_brand ? `${d.name} · ${d.oem_brand}` : d.name,
  }));

  return (
    <div className="animate-fade-in-up space-y-5 pb-4">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}><ArrowLeft className="h-5 w-5" /></Button>
        <h1 className="text-xl font-bold text-foreground sm:text-2xl">{editMode ? "Edit Lead" : "New Lead"}</h1>
      </div>

      {/* Lead type */}
      {!editMode && (
        <div className="grid grid-cols-2 gap-3">
          {[
            { v: "dealership", label: "Dealership", desc: "From a dealer", icon: Store },
            { v: "individual", label: "Individual", desc: "Vehicle owner", icon: User },
          ].map((o) => (
            <button key={o.v} onClick={() => setLeadType(o.v)}
              className={cn(
                "flex flex-col items-start gap-1 rounded-xl border-2 p-4 text-left transition-colors",
                leadType === o.v ? "border-brand bg-accent" : "border-border bg-card hover:border-brand/40"
              )}>
              <span className={cn("flex h-9 w-9 items-center justify-center rounded-lg",
                leadType === o.v ? "bg-brand text-brand-foreground" : "bg-secondary text-secondary-foreground")}>
                <o.icon className="h-4 w-4" />
              </span>
              <span className="text-sm font-bold text-foreground">{o.label}</span>
              <span className="text-xs text-muted-foreground">{o.desc}</span>
            </button>
          ))}
        </div>
      )}

      <div className="flex items-start gap-2 rounded-lg bg-accent/60 p-3 text-xs text-accent-foreground">
        <Info className="mt-0.5 h-4 w-4 shrink-0" />
        <span>Nothing is mandatory. Save with just a vehicle number now and complete the rest later.</span>
      </div>

      {/* Dealership picker */}
      {leadType === "dealership" && (
        <section className="rounded-xl border border-border bg-card p-4 shadow-card">
          <h2 className="mb-3 flex items-center gap-2 text-sm font-bold text-foreground"><Store className="h-4 w-4 text-brand" /> Dealership</h2>
          <div className="flex gap-2">
            <div className="flex-1">
              <SelectField value={dealershipId} onChange={setDealershipId} options={dealerOptions} placeholder="Select dealership" />
            </div>
            <Button variant="outline" onClick={() => setDealerDialog(true)}><Plus className="h-4 w-4" /> New</Button>
          </div>
        </section>
      )}

      {/* Customer details */}
      {leadType === "individual" && (
        <section className="rounded-xl border border-border bg-card p-4 shadow-card">
          <h2 className="mb-3 flex items-center gap-2 text-sm font-bold text-foreground"><User className="h-4 w-4 text-brand" /> Customer details</h2>
          <div className="space-y-3">
            <Field label="Customer Name"><Input className="h-11" value={form.customer_name} onChange={set("customer_name")} placeholder="Full name" /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Mobile"><Input className="h-11" type="tel" inputMode="tel" value={form.customer_mobile} onChange={set("customer_mobile")} placeholder="Mobile" /></Field>
              <Field label="Alternate No."><Input className="h-11" type="tel" inputMode="tel" value={form.customer_alt_mobile} onChange={set("customer_alt_mobile")} placeholder="Optional" /></Field>
            </div>
            <Field label="City"><Input className="h-11" value={form.customer_city} onChange={set("customer_city")} placeholder="City" /></Field>
            <Field label="Address"><Textarea rows={2} value={form.customer_address} onChange={set("customer_address")} placeholder="Address" /></Field>
          </div>
        </section>
      )}

      {/* Vehicle details */}
      <section className="rounded-xl border border-border bg-card p-4 shadow-card">
        <h2 className="mb-3 flex items-center gap-2 text-sm font-bold text-foreground"><Car className="h-4 w-4 text-brand" /> Vehicle details</h2>
        <div className="space-y-3">
          <Field label="Vehicle Number" hint="The only thing you really need to start.">
            <Input className="h-12 text-base font-semibold uppercase tracking-wide" value={form.vehicle_number} onChange={set("vehicle_number")} placeholder="DL3C AB 1234" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Make">
              <Combobox value={form.make} onChange={handleMake} options={VEHICLE_MAKES}
                placeholder="Select make" searchPlaceholder="Type to search make…" clearable />
            </Field>
            <Field label="Model">
              <Combobox value={form.model} onChange={set("model")} options={getModelsForMake(form.make)}
                placeholder={form.make ? "Select model" : "Any model"} searchPlaceholder="Type to search model…"
                emptyText={form.make ? "No match — type to add" : "Pick a make first for suggestions"} clearable />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Variant"><Input className="h-11" value={form.variant} onChange={set("variant")} placeholder="e.g. SX" /></Field>
            <Field label="Year">
              <Combobox value={form.year} onChange={set("year")} options={YEARS}
                placeholder="Year" searchPlaceholder="Search year…" allowCustom={false} clearable />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Fuel Type"><SelectField value={form.fuel_type} onChange={set("fuel_type")} options={FUEL_TYPES} placeholder="Fuel" /></Field>
            {leadType === "individual" ? (
              <Field label="KM Driven"><Input className="h-11" inputMode="numeric" value={form.km} onChange={set("km")} placeholder="e.g. 42000" /></Field>
            ) : (
              <Field label="Alloy"><SelectField value={form.alloy} onChange={set("alloy")} options={ALLOY_OPTIONS} placeholder="Alloy" /></Field>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            {leadType === "individual" && (
              <Field label="Alloy"><SelectField value={form.alloy} onChange={set("alloy")} options={ALLOY_OPTIONS} placeholder="Alloy" /></Field>
            )}
            <Field label="Condition" className={leadType === "dealership" ? "col-span-2" : ""}>
              <SelectField value={form.vehicle_condition} onChange={set("vehicle_condition")} options={CONDITIONS} placeholder="Condition" />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Demand (₹)"><Input className="h-11" inputMode="numeric" value={form.demand} onChange={set("demand")} placeholder="e.g. 180000" /></Field>
            <Field label="Offer Price (₹)"><Input className="h-11" inputMode="numeric" value={form.offer_price} onChange={set("offer_price")} placeholder="e.g. 165000" /></Field>
          </div>
          <Field label="Remarks"><Textarea rows={2} value={form.remarks} onChange={set("remarks")} placeholder="Any notes about the vehicle / deal" /></Field>
        </div>
      </section>

      <div className="flex gap-2">
        <Button variant="outline" size="xl" className="flex-1" onClick={() => navigate(-1)} disabled={saving}>Cancel</Button>
        <Button variant="brand" size="xl" className="flex-[2]" onClick={save} disabled={saving}>
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          {editMode ? "Save Changes" : "Save Lead"}
        </Button>
      </div>

      <DealershipFormDialog
        open={dealerDialog} onOpenChange={setDealerDialog}
        onSaved={async (d) => { await loadDealers(); if (d?.id) setDealershipId(d.id); }}
      />
    </div>
  );
}
